import React, { useState, useMemo } from "react";
import { TemplateItem } from "../../interfaces/templateBasedExtraction";
import { isAiStatusInProgress } from "../../utils/utils";

export interface ActiveTemplateTab extends TemplateItem {
  mode: "edit" | "run" | "regenerate";
  isCollateralBacked?: boolean;
}

export const getTemplateTabDisplayName = (
  tabs: ActiveTemplateTab[],
  tabIndex: number,
) => {
  return (tabs[tabIndex]?.name || "Template Extraction").trim();
};

interface UseTemplateTabsProps {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  collaterals?: any[];
  selectedRecording?: string | null;
}

export const useTemplateTabs = ({
  selectedTab,
  setSelectedTab,
  collaterals,
  selectedRecording,
}: UseTemplateTabsProps) => {
  const [templateAnchorEl, setTemplateAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [activeTemplateTabs, setActiveTemplateTabs] = useState<
    ActiveTemplateTab[]
  >([]);

  const previousRecordingRef = React.useRef(selectedRecording);
  React.useEffect(() => {
    if (previousRecordingRef.current === selectedRecording) return;
    previousRecordingRef.current = selectedRecording;
    setActiveTemplateTabs([]);
    if (selectedTab.startsWith("template-")) {
      setSelectedTab(selectedRecording ? "extract-callStats" : "meeting-details");
    }
  }, [selectedRecording, selectedTab, setSelectedTab]);

  const previousSelectedTabRef = React.useRef(selectedTab);
  React.useEffect(() => {
    const previousSelectedTab = previousSelectedTabRef.current;
    previousSelectedTabRef.current = selectedTab;
    if (
      previousSelectedTab === selectedTab ||
      !previousSelectedTab.startsWith("template-")
    ) {
      return;
    }

    const previousTemplateId = previousSelectedTab.replace(/^template-/, "");
    setActiveTemplateTabs((prev) => {
      let changed = false;
      const updated = prev.map((tab) => {
        if (tab.id !== previousTemplateId || tab.mode !== "regenerate") {
          return tab;
        }
        changed = true;
        return { ...tab, mode: "run" as const };
      });
      return changed ? updated : prev;
    });
  }, [selectedTab]);

  // Synchronize collaterals of type template_based_extraction_v2
  React.useEffect(() => {
    if (!Array.isArray(collaterals)) return;

    const templateCollaterals = collaterals.filter((item: any) => {
      if (item.assetType !== "template_based_extraction_v2") return false;
      if (selectedRecording && item.interactionGroupId !== selectedRecording) {
        return false;
      }
      return true;
    });

    setActiveTemplateTabs((prev) => {
      let hasChange = false;
      const currentAssetIds = new Set(
        templateCollaterals
          .map((col: any) => col.interactionAssetId)
          .filter(Boolean),
      );
      let updated = prev.filter((tab) => {
        if (!tab.isCollateralBacked) return true;
        if (
          selectedRecording &&
          tab.interactionGroupId !== selectedRecording
        ) {
          return true;
        }
        const tabAssetId = tab.assetId || tab.id;
        if (currentAssetIds.has(tabAssetId)) return true;
        hasChange = true;
        return false;
      });

      templateCollaterals.forEach((col: any) => {
        const assetId = col.interactionAssetId;
        const templateName = col.assetName || "Template Extraction";
        const templateId = col.assetMetaData?.templateId || assetId;
        const colPrompt =
          col.assetMetaData?.templatePrompt ||
          col.assetMetaData?.prompt ||
          col.assetMetaData?.extraction_prompt ||
          col.assetMetaData?.extractionPrompt ||
          col.prompt ||
          "";
        const status =
          col.aiAnalysisStatus ||
          col.assetMetaData?.aiAnalysisStatus ||
          col.assetMetaData?.ai_analysis_status ||
          col.assetMetaData?.status ||
          col.status;

        const existingIndex = updated.findIndex(
          (t) =>
            t.mode !== "edit" &&
            (t.assetId === assetId ||
              t.id === assetId ||
              (!t.assetId &&
                !t.isNewRun &&
                (t.id === templateId || t.templateId === templateId))),
        );

        const normalizedStatus =
          typeof status === "string" ? status.trim().toLowerCase() : "";
        const isInProgress = isAiStatusInProgress(status);
        const isFailed = normalizedStatus === "failed";

        if (existingIndex !== -1) {
          const current = updated[existingIndex];
          const newName = templateName || current.name;
          const newPrompt = current.prompt || colPrompt;
          const newIsGenerating = isInProgress && !isFailed;
          if (
            current.name === newName &&
            current.prompt === newPrompt &&
            current.assetId === assetId &&
            current.templateId === templateId &&
            current.aiAnalysisStatus === status &&
            current.isGenerating === newIsGenerating &&
            current.isCollateralBacked
          ) {
            return;
          }
          hasChange = true;
          updated[existingIndex] = {
            ...current,
            name: newName,
            prompt: newPrompt,
            assetId,
            templateId,
            isCollateralBacked: true,
            interactionGroupId: col.interactionGroupId || current.interactionGroupId,
            isNewRun: false,
            aiAnalysisStatus: status,
            isGenerating: newIsGenerating,
          };
        } else {
          hasChange = true;
          updated.push({
            id: assetId,
            name: templateName,
            prompt: colPrompt,
            assetId,
            templateId,
            scope: col.assetMetaData?.extraction_scope,
            interactionGroupId: col.interactionGroupId,
            mode: "run" as const,
            isCollateralBacked: true,
            aiAnalysisStatus: status,
            isGenerating: isInProgress && !isFailed,
          });
        }
      });

      return hasChange ? updated : prev;
    });
  }, [collaterals, selectedRecording]);

  const selectedActiveTemplateTab = useMemo(() => {
    if (
      !selectedTab.startsWith("template-") ||
      selectedTab === "template-generate"
    ) {
      return null;
    }
    const templateId = selectedTab.replace(/^template-/, "");
    const tabIndex = activeTemplateTabs.findIndex((t) => t.id === templateId);
    if (tabIndex === -1) return null;
    const tab = activeTemplateTabs[tabIndex];

    const displayName = getTemplateTabDisplayName(activeTemplateTabs, tabIndex);

    return {
      ...tab,
      displayName,
    };
  }, [selectedTab, activeTemplateTabs]);

  React.useEffect(() => {
    if (
      !selectedTab.startsWith("template-") ||
      selectedTab === "template-generate"
    ) {
      return;
    }
    const selectedTemplateId = selectedTab.replace(/^template-/, "");
    if (activeTemplateTabs.some((tab) => tab.id === selectedTemplateId)) return;

    setSelectedTab(
      activeTemplateTabs.length > 0
        ? `template-${activeTemplateTabs[activeTemplateTabs.length - 1].id}`
        : "abstract-insights",
    );
  }, [activeTemplateTabs, selectedTab, setSelectedTab]);

  const handleTemplateGenerateClick = (
    event: React.SyntheticEvent<HTMLElement>,
  ) => {
    event.stopPropagation();
    const target =
      (event.currentTarget.querySelector(
        ".template-generate-label",
      ) as HTMLElement) ||
      (event.currentTarget.querySelector(".tab-label-box") as HTMLElement) ||
      event.currentTarget;
    setTemplateAnchorEl(templateAnchorEl ? null : target);
  };

  const handleTemplatePopperClose = React.useCallback(() => {
    setTemplateAnchorEl(null);
  }, []);

  const handleEditTemplate = (template: TemplateItem) => {
    setTemplateAnchorEl(null);
    const sameTemplate = (tab: ActiveTemplateTab) =>
      tab.id === template.id ||
      tab.templateId === template.id ||
      Boolean(template.templateId && tab.templateId === template.templateId);
    const hasRunningOrCompletedTab = activeTemplateTabs.some(
      (tab) =>
        sameTemplate(tab) &&
        (tab.mode === "run" || tab.mode === "regenerate" || Boolean(tab.assetId)),
    );
    const existingEditTab = activeTemplateTabs.find(
      (tab) => tab.mode === "edit" && sameTemplate(tab),
    );

    let targetTabId = template.id;
    if (hasRunningOrCompletedTab && existingEditTab) {
      targetTabId = existingEditTab.id;
    } else if (hasRunningOrCompletedTab) {
      targetTabId = `${template.id}-edit-${Date.now()}`;
      setActiveTemplateTabs((prev) => [
        ...prev,
        {
          ...template,
          id: targetTabId,
          templateId: template.templateId || template.id,
          interactionGroupId: template.interactionGroupId || selectedRecording || undefined,
          mode: "edit" as const,
        },
      ]);
    } else {
      const existingTab = activeTemplateTabs.some((tab) => tab.id === template.id);
      setActiveTemplateTabs((prev) =>
        existingTab
          ? prev.map((tab) =>
              tab.id === template.id
                ? { ...tab, ...template, mode: "edit" as const }
                : tab,
            )
          : [
              ...prev,
              {
                ...template,
                interactionGroupId: template.interactionGroupId || selectedRecording || undefined,
                mode: "edit" as const,
              },
            ],
      );
    }

    setSelectedTab(`template-${targetTabId}`);
  };

  const handleRunTemplate = (
    template: TemplateItem,
    options?: { regenerate?: boolean; alreadyStarted?: boolean } | boolean,
  ) => {
    setTemplateAnchorEl(null);
    const isRegenerate =
      typeof options === "boolean"
        ? options
        : Boolean(options?.regenerate || template.shouldRegenerate);
    const alreadyStarted =
      typeof options === "boolean" ? false : Boolean(options?.alreadyStarted);
    const normalizedStatus = (
      template.aiAnalysisStatus ||
      ""
    )
      .toLowerCase()
      .trim();
    const isCompleted = normalizedStatus === "completed";
    const isFailed = normalizedStatus === "failed";

    const exactTab = activeTemplateTabs.find((tab) => tab.id === template.id);
    const canReuseExactTab =
      Boolean(exactTab && alreadyStarted && exactTab.mode !== "edit");
    const hasExistingRun = activeTemplateTabs.some(
      (tab) =>
        (tab.id === template.id ||
          tab.templateId === template.id ||
          Boolean(template.templateId && tab.templateId === template.templateId)) &&
        (tab.mode === "run" || Boolean(tab.assetId)),
    );

    const targetTabId = canReuseExactTab
      ? template.id
      : hasExistingRun || Boolean(exactTab)
        ? `${template.id}-run-${Date.now()}`
        : template.id;

    setActiveTemplateTabs((prev) => {
      if (canReuseExactTab) {
        return prev.map((tab) =>
          tab.id === template.id
            ? {
                ...tab,
                ...template,
                assetId: template.assetId || tab.assetId,
                templateId: template.templateId || tab.templateId,
                mode: "run" as const,
                isNewRun: !template.assetId && !tab.assetId,
                isGenerating:
                  template.isGenerating ??
                  (isRegenerate || (!isCompleted && !isFailed)),
                shouldRegenerate: alreadyStarted ? false : isRegenerate,
              }
            : tab,
        );
      }

      if (hasExistingRun || exactTab) {
        return [
          ...prev,
          {
            ...template,
            id: targetTabId,
            templateId: template.templateId || template.id,
            interactionGroupId: template.interactionGroupId || selectedRecording || undefined,
            isNewRun: true,
            assetId: undefined,
            mode: "run" as const,
            isGenerating: true,
            shouldRegenerate: false,
          },
        ];
      }

      return [
        ...prev,
        {
          ...template,
          id: targetTabId,
          templateId: template.templateId || template.id,
          interactionGroupId: template.interactionGroupId || selectedRecording || undefined,
          isNewRun: !template.assetId,
          mode: "run" as const,
          isGenerating: isRegenerate || (!isCompleted && !isFailed),
          shouldRegenerate: isRegenerate,
        },
      ];
    });

    setSelectedTab(`template-${targetTabId}`);
  };

  const handleViewTemplateExtraction = (template: TemplateItem) => {
    setTemplateAnchorEl(null);
    if (!template.assetId) return;

    const existingExtractionTab = activeTemplateTabs.find(
      (tab) => tab.assetId === template.assetId || tab.id === template.assetId,
    );
    if (existingExtractionTab) {
      setSelectedTab(`template-${existingExtractionTab.id}`);
      return;
    }

    setActiveTemplateTabs((prev: any) => [
      ...prev.filter((tab: any) => tab.assetId !== template.assetId && tab.id !== template.assetId),
      {
        ...template,
        id: template.assetId,
        templateId: template.templateId || template.id,
        mode: "run" as const,
        isNewRun: false,
        isGenerating: false,
      },
    ]);
    setSelectedTab(`template-${template.assetId}`);
  };

  const handleCreateNewTemplate = () => {
    setTemplateAnchorEl(null);
    const newTemplateId = "new-template";
    setActiveTemplateTabs((prev) => {
      const exists = prev.some((t) => t.id === newTemplateId);
      if (exists) {
        return prev.map((t) =>
          t.id === newTemplateId ? { ...t, mode: "edit" as const } : t,
        );
      }
      return [
        ...prev,
        {
          id: newTemplateId,
          name: "New template",
          prompt: "",
          mode: "edit" as const,
        },
      ];
    });
    setSelectedTab(`template-${newTemplateId}`);
  };

  const handleSaveSuccess = (saved: TemplateItem) => {
    setActiveTemplateTabs((prev) =>
      prev.map((t) =>
        t.id === saved.id ||
        (t.id === "new-template" && saved.id !== "new-template")
          ? { ...saved, mode: "edit" as const }
          : t,
      ),
    );
    if (saved.id !== "new-template") {
      setSelectedTab(`template-${saved.id}`);
    }
  };

  const handleCancelTemplateEdit = () => {
    if (!selectedActiveTemplateTab) return;

    if (selectedActiveTemplateTab.mode === "regenerate") {
      setActiveTemplateTabs((prev) =>
        prev.map((tab) =>
          tab.id === selectedActiveTemplateTab.id
            ? { ...tab, mode: "run" as const }
            : tab,
        ),
      );
      return;
    }

    const remaining = activeTemplateTabs.filter(
      (tab) => tab.id !== selectedActiveTemplateTab.id,
    );
    setActiveTemplateTabs(remaining);

    if (
      selectedActiveTemplateTab.id === "new-template" ||
      !selectedActiveTemplateTab.assetId
    ) {
      setSelectedTab(
        remaining.length > 0
          ? `template-${remaining[remaining.length - 1].id}`
          : "abstract-insights",
      );
      return;
    }

    // Editing an extraction opens a separate edit tab. Cancel closes that
    // temporary tab without taking the user back to the extraction they came from.
    const sameTemplate = (tab: ActiveTemplateTab) =>
      tab.templateId === selectedActiveTemplateTab.templateId ||
      tab.id === selectedActiveTemplateTab.templateId;
    const otherTemplateTab = [...remaining]
      .reverse()
      .find((tab) => !sameTemplate(tab));
    setSelectedTab(
      otherTemplateTab ? `template-${otherTemplateTab.id}` : "abstract-insights",
    );
  };

  const handleSwitchToEdit = (template: TemplateItem) => {
    setActiveTemplateTabs((prev) =>
      prev.map((t) =>
        t.id === template.id
          ? {
              ...t,
              ...template,
              assetId: template.assetId || t.assetId,
              mode: "edit" as const,
            }
          : t,
      ),
    );
  };

  const handleSwitchToRegenerate = (template: TemplateItem) => {
    setActiveTemplateTabs((prev) =>
      prev.map((t) => {
        const isMatch =
          t.id === template.id ||
          (template.assetId &&
            (t.assetId === template.assetId || t.id === template.assetId));
        return isMatch
          ? {
              ...t,
              ...template,
              assetId: template.assetId || t.assetId,
              templateId: template.templateId || t.templateId,
              mode: "regenerate" as const,
            }
          : t;
      }),
    );
  };

  const handleDeleteSuccess = (deletedTemplate: TemplateItem) => {
    const remaining = activeTemplateTabs.filter(
      (t) =>
        t.id !== deletedTemplate.id &&
        (!deletedTemplate.assetId || t.assetId !== deletedTemplate.assetId),
    );
    setActiveTemplateTabs((prev) =>
      prev.filter(
        (t) =>
          t.id !== deletedTemplate.id &&
          (!deletedTemplate.assetId || t.assetId !== deletedTemplate.assetId),
      ),
    );
    setSelectedTab(
      remaining.length > 0
        ? `template-${remaining[remaining.length - 1].id}`
        : "abstract-insights",
    );
  };

  const handleUpdateAssetId = React.useCallback(
    (templateId: string, assetId: string) => {
      if (!templateId || !assetId) return;
      setActiveTemplateTabs((prev) => {
        let hasChange = false;
        const updated = prev.map((t) => {
          if (t.id === templateId) {
            if (t.assetId === assetId) return t;
            hasChange = true;
            return { ...t, assetId, interactionAssetId: assetId, isNewRun: false };
          }
          return t;
        });
        return hasChange ? updated : prev;
      });
    },
    [],
  );

  const handleUpdateStatus = React.useCallback(
    (templateId: string, status: string) => {
      if (!templateId || !status) return;
      setActiveTemplateTabs((prev) => {
        let hasChange = false;
        const updated = prev.map((t) => {
          if (t.id === templateId || t.assetId === templateId) {
            const isInProgress = isAiStatusInProgress(status);
            if (
              t.aiAnalysisStatus === status &&
              t.isGenerating === isInProgress
            ) {
              return t;
            }
            hasChange = true;
            return {
              ...t,
              status,
              aiAnalysisStatus: status,
              isGenerating: isInProgress,
            };
          }
          return t;
        });
        return hasChange ? updated : prev;
      });
    },
    [],
  );

  return {
    templateAnchorEl,
    activeTemplateTabs,
    selectedActiveTemplateTab,
    handleTemplateGenerateClick,
    handleTemplatePopperClose,
    handleEditTemplate,
    handleRunTemplate,
    handleViewTemplateExtraction,
    handleCreateNewTemplate,
    handleSaveSuccess,
    handleCancelTemplateEdit,
    handleSwitchToEdit,
    handleSwitchToRegenerate,
    handleDeleteSuccess,
    handleUpdateAssetId,
    handleUpdateStatus,
  };
};

export default useTemplateTabs;
